import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import AssignmentReturnedIcon from "@mui/icons-material/AssignmentReturned";
import toast from "react-hot-toast";
import api from "../services/api";

const money = (value) => `₹${Number(value || 0).toFixed(2)}`;
const dateText = (value) => (value ? new Date(value).toLocaleDateString("en-IN") : "-");

const DepositManagement = () => {
  const [dashboard, setDashboard] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [backfillDialogOpen, setBackfillDialogOpen] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [returnConfirmOpen, setReturnConfirmOpen] = useState(false);
  const [returnLoading, setReturnLoading] = useState(false);
  const isAdmin = localStorage.getItem("role") === "admin";
  const [filters, setFilters] = useState({
    search: "",
    module_type: "",
    deposit_status: "",
    admission_status: "",
  });
  const [receiveForm, setReceiveForm] = useState({ paid_amount: "", payment_mode: "", payment_reference: "", remarks: "" });
  const [returnForm, setReturnForm] = useState({ deduction_amount: "", deduction_reason: "", remarks: "" });

  const returnAmountPreview = useMemo(() => {
    if (!selectedDeposit) return 0;
    const depositAmount = Number(selectedDeposit.deposit_amount || 0);
    const deduction = Number(returnForm.deduction_amount || 0);
    return Math.max(depositAmount - deduction, 0);
  }, [selectedDeposit, returnForm.deduction_amount]);

  const fetchDeposits = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== ""));
      const [dashboardRes, listRes] = await Promise.all([
        api.get("/deposit/dashboard/", { params }),
        api.get("/deposit/", { params }),
      ]);
      setDashboard(dashboardRes.data || {});
      setRows(listRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load deposits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeposits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetails = async (deposit) => {
    try {
      const res = await api.get(`/deposit/${deposit.id}/`);
      const data = res.data;
      setSelectedDeposit(data);
      setReceiveForm({
        paid_amount: data.paid_amount || data.deposit_amount || "",
        payment_mode: data.payment_mode || "",
        payment_reference: data.payment_reference || "",
        remarks: data.remarks || "",
      });
      setReturnForm({ deduction_amount: "", deduction_reason: "", remarks: data.remarks || "" });
    } catch (error) {
      toast.error("Failed to open deposit details");
    }
  };

  const receiveDeposit = async () => {
    if (!selectedDeposit) return;
    try {
      const payload = { ...receiveForm };
      await api.patch(`/deposit/${selectedDeposit.id}/receive/`, payload);
      toast.success("Deposit received successfully");
      setSelectedDeposit(null);
      fetchDeposits();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Deposit receive failed");
    }
  };

  const returnDeposit = async () => {
    if (!selectedDeposit || returnLoading) return;

    setReturnLoading(true);
    try {
      const payload = { ...returnForm };
      await api.patch(`/deposit/${selectedDeposit.id}/return/`, payload);
      toast.success("Deposit returned successfully");
      setReturnConfirmOpen(false);
      setSelectedDeposit(null);
      fetchDeposits();
    } catch (error) {
      const data = error.response?.data;
      toast.error(data?.detail || data?.non_field_errors?.[0] || data?.deduction_reason || data?.deduction_amount || data?.returned_amount || "Deposit return failed");
    } finally {
      setReturnLoading(false);
    }
  };

  const backfillOldDeposits = async () => {
    setBackfillLoading(true);
    setBackfillResult(null);
    try {
      const res = await api.post("/deposit/backfill/");
      setBackfillResult(res.data);
      toast.success(res.data?.message || "Deposit backfill completed successfully");
      fetchDeposits();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Deposit backfill failed");
    } finally {
      setBackfillLoading(false);
    }
  };

  const Card = ({ title, value, icon, color }) => (
    <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100 hover:shadow-xl transition">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm text-gray-500 font-semibold">{title}</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
        </div>
        <div className={`p-3 rounded-xl ${color}`}>{icon}</div>
      </div>
    </div>
  );

  const statusBadge = (status) => {
    const map = {
      pending: "bg-yellow-100 text-yellow-800",
      paid: "bg-green-100 text-green-800",
      returned: "bg-blue-100 text-blue-800",
    };
    return <span className={`px-3 py-1 rounded-full text-xs font-bold ${map[status] || "bg-gray-100 text-gray-700"}`}>{String(status || "-").replaceAll("_", " ")}</span>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-3 md:p-6 space-y-5">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-white rounded-xl shadow-lg p-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Deposit Management</h2>
          <p className="text-sm text-gray-500">Track hostel and library security deposits separately from invoices and revenue.</p>
        </div>
        <Button variant="contained" color="secondary" onClick={() => { setBackfillResult(null); setBackfillDialogOpen(true); }}>
          Backfill Old Deposits
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <Card title="Total Deposits" value={money(dashboard.total_deposits)} icon={<CurrencyRupeeIcon className="text-white" />} color="bg-indigo-600" />
        <Card title="Paid Deposits" value={money(dashboard.paid_deposits)} icon={<AccountBalanceWalletIcon className="text-white" />} color="bg-green-600" />
        <Card title="Pending Deposits" value={money(dashboard.pending_deposits)} icon={<HourglassEmptyIcon className="text-white" />} color="bg-yellow-600" />
        <Card title="Returned Deposits" value={money(dashboard.returned_deposits)} icon={<AssignmentReturnedIcon className="text-white" />} color="bg-blue-600" />
        <Card title="Hostel Deposits" value={money(dashboard.hostel_deposits)} icon={<AccountBalanceWalletIcon className="text-white" />} color="bg-purple-600" />
        <Card title="Library Deposits" value={money(dashboard.library_deposits)} icon={<AccountBalanceWalletIcon className="text-white" />} color="bg-teal-600" />
      </div>

      <div className="bg-white rounded-xl shadow-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <input className="border rounded-lg px-3 py-2" placeholder="Search name, username, deposit ID or booking ID" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} onKeyDown={(e) => e.key === "Enter" && fetchDeposits()} />
          <select className="border rounded-lg px-3 py-2 bg-white" value={filters.module_type} onChange={(e) => setFilters({ ...filters, module_type: e.target.value })}>
            <option value="">Hostel / Library</option>
            <option value="hostel">Hostel</option>
            <option value="library">Library</option>
          </select>
          <select className="border rounded-lg px-3 py-2 bg-white" value={filters.deposit_status} onChange={(e) => setFilters({ ...filters, deposit_status: e.target.value })}>
            <option value="">Deposit Status</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="returned">Returned</option>
          </select>
          <select className="border rounded-lg px-3 py-2 bg-white" value={filters.admission_status} onChange={(e) => setFilters({ ...filters, admission_status: e.target.value })}>
            <option value="">Admission Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="cancelled">Cancelled</option>
            <option value="expired">Expired</option>
            <option value="completed">Completed</option>
            <option value="left">Left</option>
          </select>
          <Button variant="contained" onClick={fetchDeposits}>Search</Button>
        </div>
      </div>

      <div className="overflow-x-auto bg-white rounded-xl shadow-lg border border-gray-100">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="p-3 text-left">Deposit ID</th>
              <th className="p-3 text-left">Student</th>
              <th className="p-3 text-left">Phone</th>
              <th className="p-3 text-left">Hostel / Library</th>
              <th className="p-3 text-left">Booking ID</th>
              <th className="p-3 text-left">Deposit Amount</th>
              <th className="p-3 text-left">Returned Amount</th>
              <th className="p-3 text-left">Deposit Status</th>
              <th className="p-3 text-left">Paid Date</th>
              <th className="p-3 text-left">Returned Date</th>
              <th className="p-3 text-left">Returned By</th>
              <th className="p-3 text-left">Admission Status</th>
              <th className="p-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="13" className="p-6 text-center text-gray-500">Loading deposits...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="13" className="p-6 text-center text-gray-500">No deposit records found</td></tr>
            ) : rows.map((item) => (
              <tr key={item.id} className="border-t hover:bg-gray-50">
                <td className="p-3 font-semibold">{item.deposit_id || `DEP-${item.id}`}</td>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <Avatar sx={{ width: 34, height: 34 }}>{(item.student_name || "S").charAt(0)}</Avatar>
                    <div>
                      <div className="font-bold text-gray-800">{item.student_name}</div>
                      <div className="text-xs text-gray-500">{item.student_username}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3">{item.phone || "-"}</td>
                <td className="p-3 capitalize font-semibold">{item.module_type}</td>
                <td className="p-3">#{item.booking_id}</td>
                <td className="p-3 font-bold">{money(item.deposit_amount)}</td>
                <td className="p-3 font-bold text-blue-700">{money(item.returned_amount)}</td>
                <td className="p-3">{statusBadge(item.deposit_status)}</td>
                <td className="p-3">{dateText(item.paid_date)}</td>
                <td className="p-3">{dateText(item.returned_date)}</td>
                <td className="p-3">{item.returned_by_name || "-"}</td>
                <td className="p-3 capitalize">{item.admission_status || "-"}</td>
                <td className="p-3"><Button size="small" variant="outlined" onClick={() => openDetails(item)}>View</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={backfillDialogOpen} onClose={() => !backfillLoading && setBackfillDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle className="font-bold">Backfill Old Deposits</DialogTitle>
        <DialogContent dividers>
          <p className="text-sm text-gray-700 mb-3">
            This will create missing deposit records for old Hostel and Library bookings.
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-900 space-y-1">
            <p>It will not affect Payment Report, Revenue Report, Invoice Generation, Billing Settings, Renewal, or Authentication.</p>
            <p>Hostel deposit exists/source found → Library deposit will be skipped.</p>
            <p>First month already crossed → Deposit will still be backfilled from original booking deposit amount.</p>
            <p>Running this multiple times will not create duplicates.</p>
          </div>

          {backfillResult && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-900">
              <p className="font-bold mb-2">{backfillResult.message || "Backfill completed."}</p>
              <div className="grid grid-cols-2 gap-2">
                <p><b>Processed:</b> {backfillResult.summary?.processed ?? 0}</p>
                <p><b>Created:</b> {backfillResult.summary?.created ?? 0}</p>
                <p><b>Skipped:</b> {backfillResult.summary?.skipped ?? 0}</p>
                <p><b>Failed:</b> {backfillResult.summary?.failed ?? 0}</p>
                <p><b>Hostel Created:</b> {backfillResult.details?.hostel_created ?? 0}</p>
                <p><b>Library Created:</b> {backfillResult.details?.library_created ?? 0}</p>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={backfillLoading} onClick={() => setBackfillDialogOpen(false)}>
            {backfillResult ? "Close" : "Cancel"}
          </Button>
          {!backfillResult && (
            <Button variant="contained" disabled={backfillLoading} onClick={backfillOldDeposits}>
              {backfillLoading ? "Backfilling..." : "Start Backfill"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(selectedDeposit)} onClose={() => setSelectedDeposit(null)} maxWidth="md" fullWidth>
        <DialogTitle className="font-bold">Deposit Details</DialogTitle>
        {selectedDeposit && (
          <DialogContent dividers>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <Box className="bg-gray-50 rounded-xl p-4 space-y-2">
                <Typography variant="h6">Student Details</Typography>
                <p><b>Deposit ID:</b> {selectedDeposit.deposit_id}</p>
                <p><b>Student Name:</b> {selectedDeposit.student_name}</p>
                <p><b>Phone:</b> {selectedDeposit.phone || "-"}</p>
                <p><b>Booking:</b> #{selectedDeposit.booking_id}</p>
                <p><b>Hostel / Library:</b> {selectedDeposit.module_type}</p>
                <p><b>Admission Date:</b> {dateText(selectedDeposit.admission_date)}</p>
                <p><b>Admission Status:</b> {selectedDeposit.admission_status || "-"}</p>
              </Box>
              <Box className="bg-gray-50 rounded-xl p-4 space-y-2">
                <Typography variant="h6">Deposit Details</Typography>
                <p><b>Deposit Amount:</b> {money(selectedDeposit.deposit_amount)}</p>
                <p><b>Returned Amount:</b> {money(selectedDeposit.returned_amount)}</p>
                <p><b>Payment Mode:</b> {selectedDeposit.payment_mode || "-"}</p>
                <p><b>Payment Reference:</b> {selectedDeposit.payment_reference || "-"}</p>
                <p><b>Paid Date:</b> {dateText(selectedDeposit.paid_date)}</p>
                <p><b>Received By:</b> {selectedDeposit.received_by_name || "-"}</p>
                <p><b>Returned Date:</b> {dateText(selectedDeposit.returned_date)}</p>
                <p><b>Returned By:</b> {selectedDeposit.returned_by_name || "-"}</p>
                <p><b>Remarks:</b> {selectedDeposit.remarks || "-"}</p>
              </Box>
            </div>

            {isAdmin && selectedDeposit.deposit_status === "pending" && (
              <Box className="mt-5 bg-green-50 rounded-xl p-4">
                <Typography variant="h6" className="mb-3">Receive Deposit</Typography>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <TextField size="small" label="Paid Amount" type="number" value={receiveForm.paid_amount} onChange={(e) => setReceiveForm({ ...receiveForm, paid_amount: e.target.value })} />
                  <TextField size="small" label="Payment Mode" value={receiveForm.payment_mode} onChange={(e) => setReceiveForm({ ...receiveForm, payment_mode: e.target.value })} />
                  <TextField size="small" label="Payment Reference" value={receiveForm.payment_reference} onChange={(e) => setReceiveForm({ ...receiveForm, payment_reference: e.target.value })} />
                  <TextField size="small" label="Remarks" value={receiveForm.remarks} onChange={(e) => setReceiveForm({ ...receiveForm, remarks: e.target.value })} />
                </div>
                <Button className="mt-3" variant="contained" onClick={receiveDeposit}>Mark as Received</Button>
              </Box>
            )}

            {isAdmin && selectedDeposit.deposit_status === "paid" && (
              <Box className="mt-5 bg-blue-50 rounded-xl p-4">
                <Typography variant="h6" className="mb-3">Return Deposit</Typography>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <TextField size="small" label="Deposit Amount" type="number" value={selectedDeposit.deposit_amount || ""} InputProps={{ readOnly: true }} />
                  <TextField size="small" label="Deduction Amount" type="number" inputProps={{ min: 0, max: Number(selectedDeposit.deposit_amount || 0), step: "0.01" }} value={returnForm.deduction_amount} onChange={(e) => setReturnForm({ ...returnForm, deduction_amount: e.target.value })} />
                  <TextField size="small" label="Returned Amount" type="number" value={returnAmountPreview} InputProps={{ readOnly: true }} />
                  <TextField size="small" label="Deduction Reason" value={returnForm.deduction_reason} onChange={(e) => setReturnForm({ ...returnForm, deduction_reason: e.target.value })} />
                  <TextField size="small" label="Remarks" value={returnForm.remarks} onChange={(e) => setReturnForm({ ...returnForm, remarks: e.target.value })} />
                </div>
                <p className="text-xs text-gray-600 mt-2">Returned Amount = Deposit Amount - Deduction Amount. The backend recalculates this value before saving.</p>
                <Button className="mt-3" variant="contained" onClick={() => setReturnConfirmOpen(true)}>Return Deposit</Button>
              </Box>
            )}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setSelectedDeposit(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={returnConfirmOpen}
        onClose={() => {
          if (!returnLoading) setReturnConfirmOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle className="font-bold">Confirm Deposit Return</DialogTitle>
        <DialogContent dividers>
          Are you sure you want to return this deposit? This action cannot be reversed.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnConfirmOpen(false)} disabled={returnLoading}>No</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={returnDeposit}
            disabled={returnLoading}
          >
            {returnLoading ? "Returning..." : "Yes, Return Deposit"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DepositManagement;
